/**
 * #5460 (ChatGPT exact-head P1 on #5453) — THE READER NAMES THE ARTIFACT,
 * NEVER THE INTENT THAT ASKED FOR IT.
 *
 * `evidenceRequirementReaderLabel` (apps/api ask-helix
 * `capability-validator.evidence-labels.ts`) used to fall back from a
 * metric-registry hit straight to the REQUESTING INTENT's label. That names
 * "what answer needed this evidence", not "what the evidence is" — so a
 * symbolic-inference read missing four distinct, unrelated requirements (an
 * approved strategy pack, the strategy relationship map, a metric, a
 * decision-realization record) rendered all four under one identical label,
 * and the executive sentence's `Set` dedupe then collapsed them into one
 * circular line: "Trace metric derivation isn't available yet: the approved
 * trace metric derivation is not yet in the evidence set."
 *
 * This registry is the fix: a DECLARATION-OWNED, executive-safe label for
 * every non-metric evidence key a governed capability can require, keyed by
 * the same `evidenceKey` string the manifest declares. A metric-keyed
 * requirement (the semantic metric-query layer declares the metric key AS
 * the evidence key) already carries an authored label in the governed metric
 * registry and is resolved there first; this registry exists for the domain
 * artifacts a rule or module reads structurally — approved packs, ontology
 * projections, ledger commitments — that are not metric keys and so have no
 * metric-registry entry.
 *
 * CLOSED and reviewed here, beside the keys it names. The static
 * `evidence-requirement-labels.repo-coverage.spec.ts` enumerates every
 * declared evidence key from EVERY production source of a
 * `GovernedCapabilityManifest` — the decision-module catalog, the symbolic
 * inference manifest, AND `buildWorkspaceContextCapabilityManifests`
 * (workspace-context-capabilities.ts, #5460 P1-1) — so an unregistered
 * non-metric key fails before it can reach the raw-key fallback in any of
 * them. Every value is a bare
 * noun phrase with no leading article — the executive sentence that consumes
 * it supplies "the approved …" itself — and none may repeat the internal
 * vocabulary the raw keys are written in ("governed_metric_fact",
 * "…_binding"): that is exactly why a humanized key is not an executive-safe
 * label and a declared one is required. See
 * `evidence-requirement-reader-labels.spec.ts` for the closed-list and
 * banned-vocabulary checks.
 */
export const EVIDENCE_REQUIREMENT_READER_LABELS: Readonly<Record<string, string>> = Object.freeze({
  // capacity-utilization
  approved_capacity_utilization_snapshot: 'capacity utilization snapshot',

  // capital-allocation
  approved_capex_pipeline_snapshot: 'capital investment pipeline snapshot',
  approved_wacc_hurdle_rate: 'weighted cost of capital hurdle rate',
  capex_budget_envelope: 'capital investment budget',
  capital_expenditures_usd: 'capital expenditure record',
  project_cashflow_forecast: 'project cash flow forecast',

  // capital-cash
  approved_balance_sheet_snapshot: 'balance sheet snapshot',
  approved_capex_line: 'capital investment line item',
  approved_working_capital_components: 'working capital detail',

  // carrier-transportation-cost
  approved_external_benchmark: 'external market benchmark',
  carrier_contract_penalties: 'carrier contract penalty schedule',
  carrier_contract_terms: 'carrier agreement terms',
  carrier_rate_card: 'carrier pricing schedule',
  customer_contract_terms: 'customer agreement terms',
  customer_price_elasticity: 'customer price response analysis',
  delivery_exception_cause_attribution: 'delivery exception cause analysis',
  delivery_performance_actuals: 'actual delivery service results',
  finance_bridge: 'financial reconciliation bridge',
  human_activation_approval: 'activation approval',
  mode_accessorial_economics: 'transport accessorial cost analysis',
  mode_shift_options: 'transport mode alternatives',
  service_capacity_evidence: 'service capacity assessment',
  service_promise_register: 'service commitment record',
  service_risk: 'service risk assessment',
  shipment_lane_spend: 'shipment lane spend detail',
  specialist_network_optimizer_output: 'network optimization analysis',
  strategy_margin_floor: 'strategy margin threshold',
  tender_rule_evidence: 'tender rule documentation',
  transportation_control_plan: 'transportation operating plan',
  transportation_decision_package: 'transportation recommendation package',

  // churn-reduction
  churn_events: 'customer churn history',
  cohort_retention: 'customer cohort retention',
  save_play_assumptions: 'retention intervention assumptions',

  // commercial-effectiveness
  account_service_attach_map: 'account service adoption map',
  approved_pipeline_snapshot: 'sales pipeline snapshot',
  churn_retention_actuals: 'customer retention actuals',
  sales_cost_roster: 'commercial cost roster',

  // cost-to-serve
  account_cohort_snapshot: 'customer cohort snapshot',
  account_revenue_concentration_snapshot: 'customer revenue concentration snapshot',
  activity_cost_driver_map: 'activity cost basis map',
  approved_account_revenue_snapshot: 'customer revenue snapshot',
  approved_chart_of_accounts_mapping: 'chart of accounts mapping',
  approved_finance_operating_income_tie_out: 'operating income reconciliation',
  service_tier_cost_table: 'service-level cost table',

  // earnings-defense
  earnings_defense_answer_ledger_receipt: 'earnings defense answer record',
  earnings_defense_benchmark_packet: 'earnings defense comparison packet',
  earnings_defense_disclosure_posture: 'earnings defense disclosure assessment',
  earnings_defense_identity_resolution: 'earnings defense identity assessment',
  earnings_defense_meeting_mode_capture: 'earnings defense meeting record',
  earnings_defense_question_pattern_artifact: 'earnings defense question analysis',
  earnings_defense_review_factory_snapshot_contract: 'earnings defense review snapshot',
  earnings_defense_source_packet_registry: 'earnings defense source packet index',
  earnings_defense_variance_packet: 'earnings defense variance analysis',

  // esg-emissions
  abatement_lever_cost_table: 'emissions reduction cost table',
  approved_emission_factor_table: 'emissions factor table',
  emission_reduction_target: 'emissions reduction target',
  shipment_tonne_km_snapshot: 'shipment distance and weight snapshot',

  // finance
  // Cross-module strategy/objective link, cited by the finance manifest's
  // objective_value_gap_v1 evidence requirement and the decision ledger.
  active_strategy_objective_binding: 'strategy objective link',
  approved_cost_ratio_formula_ref: 'cost ratio calculation reference',
  approved_ebitda_definition: 'EBITDA definition',
  approved_finance_pnl_artifact: 'profit and loss statement',
  approved_scenario_funding_plan_artifact: 'scenario funding plan',
  approved_scenario_investment_requirements_artifact: 'scenario investment requirements',
  approved_scenario_value_case_artifact: 'scenario value case',
  // finance.capital-allocation-intents.ts declares this key (see its own
  // "no admission path" comment); it was never registered here (#5988
  // class 3).
  approved_buyback_return_comparator_artifact: 'buyback return comparator',

  // fuel-efficiency
  approved_fuel_efficiency_snapshot: 'fuel efficiency snapshot',

  // industry-benchmark-lens
  industry_benchmark_reference_ranges: 'industry benchmark ranges',

  // labor-arbitrage
  approved_labor_arbitrage_snapshot: 'labor cost comparison snapshot',

  // ma-deal (#5965 census completion; label added #5988 class 3)
  ma_deal_case_pack_records: 'M&A deal case record',

  // ma-synergy-tracking
  ma_fusion_synergy_underwriting: 'merger synergy assessment',
  ma_synergy_scoreboard: 'merger synergy scorecard',

  // marketing-spend
  approved_marketing_spend_snapshot: 'marketing spend snapshot',

  // network-optimization
  approved_network_optimization_snapshot: 'network optimization snapshot',

  // org-analysis-optimization
  activity_work_assignment: 'activity-to-work assignment',
  finance_anchor: 'financial baseline',
  function_taxonomy: 'function classification',
  historical_execution_actuals: 'execution history',
  initiative_capacity_binding: 'initiative capacity commitment',
  intent_evidence_manifest: 'intent evidence plan',
  operating_outcome_fact: 'operating outcome record',
  option_action_plan: 'organization action plan',
  option_cost_cash_flow: 'organization cost and cash flow',
  option_decision_policy: 'organization decision policy',
  option_definition: 'organization option definition',
  option_schedule: 'organization option schedule',
  org_baseline_snapshot: 'organization baseline snapshot',
  org_cost_sub_lever_buildup: 'organization cost driver analysis',
  org_state_as_is: 'current organization design',
  people_tenure: 'employee tenure profile',
  position_cost_baseline: 'role cost baseline',
  position_cost_roster: 'role cost roster',
  org_state_to_be: 'proposed organization design',
  neurotruth_pattern_recommendation_run:
    'organization-pattern recommendation run',
  realization_actuals: 'actual realization results',
  scenario_risk_model: 'scenario risk assessment',
  severance_policy: 'workforce separation policy',
  span_layer_metrics: 'span and layer measures',
  strategy_capability_projection: 'strategy capability outlook',
  workforce_attrition_cohort: 'employee attrition cohort',
  workforce_legal_gate: 'workforce legal approval',

  // org-combination (#5906; label added #5988 class 3)
  fusion_scenario_decision_snapshot: 'organization combination decision snapshot',

  // outside-contractors
  approved_outside_contractors_snapshot: 'outside contractor spend snapshot',

  // portfolio-capacity-allocation
  capacity_actuals_snapshot: 'actual capacity snapshot',
  dependency_constraint_snapshot: 'dependency and constraint snapshot',
  finance_actuals_snapshot: 'financial actuals snapshot',
  portfolio_plan_snapshot: 'portfolio planning snapshot',

  // pricing-realization
  approved_accessorial_charge_schedule: 'accessorial charge schedule',
  approved_contract_rate_card: 'contract rate card',
  approved_discount_policy_authority_matrix: 'discount authority matrix',
  approved_discount_transaction_snapshot: 'discount transaction snapshot',
  approved_fuel_surcharge_schedule: 'fuel surcharge schedule',
  approved_market_rate_benchmark: 'market rate benchmark',
  approved_price_action_adoption_snapshot: 'price action adoption snapshot',
  approved_price_action_register: 'price action record',
  approved_pricing_execution_cost: 'pricing execution cost',
  approved_pricing_realization_snapshot: 'pricing realization snapshot',
  approved_pricing_structure_mix_snapshot: 'pricing structure mix snapshot',
  approved_spot_to_contract_intervention_exchange: 'spot-to-contract intervention analysis',

  // productivity-lean-ai
  approved_productivity_realization_snapshot: 'productivity realization snapshot',

  // professional-fees
  approved_professional_fees_snapshot: 'professional fees snapshot',

  // real-estate-footprint
  approved_real_estate_footprint_snapshot: 'real estate footprint snapshot',

  // risk-claims
  approved_claims_register: 'claims record',
  claims_by_carrier_lane_map: 'carrier lane claims map',
  contract_liability_terms: 'contractual liability terms',
  subrogation_recovery_actuals: 'subrogation recovery results',

  // segment-margin-cleanup
  approved_segment_margin_fact_decomposition: 'segment margin detail',
  approved_segment_margin_snapshot: 'segment margin snapshot',

  // sga-cost-pool (#5965 census completion; label added #5988 class 3)
  sga_cost_pool_gold_snapshot_records: 'SG&A cost pool snapshot',

  // sga-spend
  approved_finance_agp_metric: 'adjusted gross profit measure',
  approved_indirect_procurement_snapshot: 'indirect procurement snapshot',
  approved_sga_control_plan: 'SG&A control plan',
  approved_sga_owner_map: 'SG&A owner map',
  approved_sga_policy_leakage_snapshot: 'SG&A policy leakage snapshot',
  approved_sga_program_cost_snapshot: 'SG&A program cost snapshot',
  approved_sga_q04_governed_floor: 'SG&A Q04 minimum threshold',
  approved_sga_recovery_evidence: 'SG&A recovery support',
  approved_sga_recurring_classification: 'SG&A recurring cost classification',
  approved_sga_scenario_assumptions: 'SG&A scenario assumptions',
  approved_sga_target: 'SG&A target',
  approved_tech_software_snapshot: 'technology and software spend snapshot',
  approved_tne_snapshot: 'travel and expense snapshot',
  certified_sga_lever_metrics: 'SG&A lever measures',
  finance_sga_line_reconciliation: 'SG&A finance reconciliation',
  observed_sga_spend_feed: 'SG&A spend feed',
  sga_benchmark_profile_artifact: 'SG&A benchmark profile',
  sga_cost_pool_mapping: 'SG&A cost pool mapping',
  sga_formula_runtime_receipts: 'SG&A calculation record',

  // shared-services
  approved_shared_services_snapshot: 'shared services snapshot',

  // strategic-execution-capacity-diagnostic
  allocation_policy: 'capacity allocation policy',
  capacity_baseline: 'capacity planning baseline',
  constraint_and_dependency_evidence: 'constraint and dependency assessment',
  execution_actuals: 'actual execution results',
  initiative_portfolio_plan: 'initiative portfolio roadmap',
  strategic_execution_gap_snapshot: 'strategic execution variance snapshot',

  // strategy (#5965 census completion; label added #5988 class 3)
  strategic_objective_binding: 'strategy objective assignment',

  // tech-software
  // `approved_tech_software_snapshot` is shared with sga-spend above.

  // workforce-reduction
  // `initiative_capacity_binding`, `org_state_as_is`, `people_tenure`,
  // `position_cost_roster`, `severance_policy`, and `workforce_legal_gate`
  // are shared with org-analysis-optimization above.

  // apps/api/src/symbolic-inference/symbolic-inference.intents.ts —
  // SYMBOLIC_INFERENCE_EVIDENCE_KEYS. Every entry declared there must have
  // a matching entry here; enforced by
  // apps/api/src/symbolic-inference/symbolic-inference.evidence-labels.spec.ts.
  active_strategy_pack: 'strategy pack',
  active_strategy_scope: 'active strategy scope',
  strategy_ontology_projection: 'strategy relationship map',
  governed_metric_fact: 'metric',
  decision_ledger_realization_commitment: 'decision realization record',

  // apps/api/src/ask-helix/workspace-context-capabilities.ts —
  // buildWorkspaceContextCapabilityManifests. A SECOND production source of
  // GovernedCapabilityManifest, independent of the decision-module catalog;
  // enforced by the same repo-wide census as every decision-module manifest
  // (evidence-requirement-labels.repo-coverage.spec.ts, #5460 P1-1).
  company_profile: 'company profile summary',
  strategy_pack: 'strategy pack summary',
  active_strategy_state: 'current strategy status',
  public_record_corpus: 'public records collection',
  graph_org_bridge_reference: 'organization structure linkage',
  governed_workforce_reduction_engine: 'workforce reduction calculation',
  graph_rif_reach_context_edges: 'workforce reduction reach analysis',
});

/**
 * The declared reader label for one evidence key, or `null` when the key is
 * not a registered domain artifact (a metric key, or a key not yet
 * registered here). `evidenceRequirementReaderLabel` checks the governed
 * metric registry first, this registry second, and falls back to a
 * humanized key last — never to the requesting intent's label.
 */
export function evidenceRequirementDeclaredLabel(evidenceKey: string): string | null {
  const declared = EVIDENCE_REQUIREMENT_READER_LABELS[evidenceKey];
  return typeof declared === 'string' && declared.trim().length > 0 ? declared : null;
}
